import { getRouteApi } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import LinkText from '@/components/LinkText/LinkText'
import Table, { type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import CommentListExpandedRow from '@/components/common/CommentListExpandedRow/CommentListExpandedRow'
import CommentModal from '@/components/common/CommentModal/CommentModal'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import { RemoveCommentModal } from '@/components/common/RemoveCommentModal'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { useColumns } from '@/hooks/useColumns'
import useCommonCommentFilters from '@/hooks/useCommonCommentFilters'
import useCommonCommentsColumn from '@/hooks/useCommonCommentsColumn'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import useCommentsDownload from '@/mutations/useCommentsDowload'
import useComments, { type Comment } from '@/queries/useComments'
import useCommentsCount from '@/queries/useCommentsCount'
import useGroup from '@/queries/useGroup'
import useStudentsOptions from '@/queries/useStudentOptions'
import useAuthStore from '@/store/useAuthStore'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'
import { getFilterValue } from '@/utils/get-filter-value'

import { type CommentFiltersKeys } from '../constants/comment-filters'
import { hasEditGroupPermission } from '../utils/permissions'

const routeApi = getRouteApi('/_auth/timetable/$courseId/$groupId/comments')

const CommentsView = () => {
  const { t } = useTranslation(['lessonDetails', 'common'])
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()
  const { groupId } = routeApi.useParams()

  const { isMobile } = useScreenResolution()

  const { data: group, isLoading: isGroupLoading } = useGroup(groupId)
  const user = useAuthStore(store => store.user)

  const selectedSemester = group?.semesters.find(
    semester => semester.id === search.semester
  )

  const canChangeGroup = useMemo(
    () => hasEditGroupPermission(user, group),
    [user, group]
  )

  const canChangeComments = selectedSemester?.status !== 'archived'

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentComment, setCurrentComment] = useState<Comment | null>(null)
  const [isOpenRemoveCommentModal, setIsOpenRemoveCommentModal] =
    useState(false)

  const {
    data: comments,
    isLoading: isCommentsLoading,
    refetch: refetchComments
  } = useComments({
    groupId,
    ...search
  })

  const {
    data: commentsCount,
    isLoading: isCommentsCountLoading,
    refetch: refetchCommentsCount
  } = useCommentsCount({
    semester: search.semester,
    groupId
  })

  const { data: students, isLoading: isStudentsLoading } = useStudentsOptions({
    groupId,
    semester: search.semester
  })

  const refetchAllComments = () => {
    refetchComments()
    refetchCommentsCount()
    refetchAddedByOptions()
  }

  const pagination = usePagination(routeApi)

  const { commonCommentsColumns } = useCommonCommentsColumn()

  const { columns, ...manageColumns } = useColumns<Comment>(
    [
      {
        key: 'student',
        label: t('header.student'),
        width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
        render: (value: Comment) => (
          <LinkText
            to="/students-and-classes/students/$studentId/details"
            params={{
              studentId: value.student.id
            }}
          >
            {value.student.name}
          </LinkText>
        )
      },
      ...commonCommentsColumns
    ],
    { routeApi }
  )

  const handleSemesterChange = (value: string) => {
    navigate({
      search: {
        semester: value,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const handleChangeFilter = (
    key: CommentFiltersKeys,
    value?: string | string[] | Date
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleClearAll = () => {
    navigate({
      search: {
        semester: group?.currentSemester.id || '',
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const { commonCommentsFilters, refetchAddedByOptions } =
    useCommonCommentFilters({
      ...search,
      groupId,
      semester: group?.semesters?.find(item => item.id === search.semester),
      onChangeFilter: handleChangeFilter
    })

  const filters: Filter[] = [
    {
      label: t('label.semester'),
      variant: 'semester',
      size: 'wide',
      filterProps: {
        id: 'semester',
        loading: isGroupLoading,
        options: group?.semestersOptions || [],
        semesterList: group?.semesters || [],
        value: search.semester,
        onChange: handleSemesterChange
      }
    },
    {
      label: t('label.student'),
      variant: 'multiselect',
      size: 'wide',
      filterProps: {
        id: 'student',
        dataTestId: 'student-filter',
        multiple: true,
        loading: isStudentsLoading,
        options: students || [],
        value: search.studentsId,
        placeholder: t('label.student'),
        onChange: value => handleChangeFilter('studentsId', value)
      }
    },
    ...commonCommentsFilters
  ]

  const isSomeFilterSelected = checkIfFilterSelected(filters, ['semester'])

  const closeModal = () => {
    setIsModalOpen(false)
    setCurrentComment(null)
  }

  const actions: (row: Comment) => TableAction<Comment>[] = row =>
    canChangeComments && canChangeGroup
      ? [
          {
            icon: <EditIcon />,
            text: t('button.edit'),
            hidden: row.addedBy.id !== user?.id && !user?.isSuperAdmin,
            onClick: (item: Comment) => {
              setCurrentComment(item)
              setIsModalOpen(true)
            }
          },
          {
            icon: <DeleteIcon />,
            text: t('button.remove'),
            variantAction: 'danger' as const,
            hidden: row.addedBy.id !== user?.id && !user?.isSuperAdmin,
            onClick: (item: Comment) => {
              setCurrentComment(item)
              setIsOpenRemoveCommentModal(true)
            }
          }
        ].filter(item => !item.hidden)
      : []

  const download = useCommentsDownload({
    ...search,
    groupId,
    columnsKey: manageColumns.activeColumnKeys
  })

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        actionsRender={
          <>
            <ExportButton {...download} />

            {canChangeGroup ? (
              <CommentModal
                semester={search.semester}
                groupId={groupId}
                open={isModalOpen}
                onOpenChange={value => {
                  if (value) setIsModalOpen(value)
                  else closeModal()
                }}
                onUpdate={refetchAllComments}
                commentId={currentComment?.id}
                trigger={
                  <Button icon={<AddIcon />} disabled={!canChangeComments}>
                    {t('common:button.add-comment')}
                  </Button>
                }
              />
            ) : null}
          </>
        }
        manageColumns={manageColumns}
      />
      <Table
        id="comments-table"
        data={comments?.list || []}
        isLoading={isCommentsLoading || isCommentsCountLoading}
        columns={columns}
        actions={actions}
        count={comments?.count}
        totalCount={commentsCount}
        {...pagination}
        expandedRowRender={record => (
          <CommentListExpandedRow comment={record} />
        )}
        caption={t('header.comments')}
      />

      {currentComment ? (
        <RemoveCommentModal
          open={isOpenRemoveCommentModal}
          onOpenChange={setIsOpenRemoveCommentModal}
          commenId={currentComment.id}
          onSuccess={refetchAllComments}
        />
      ) : null}
    </>
  )
}

export default CommentsView
